/**
 * 내 계정과 직원 계정 (공통/보안과개인정보 1-4, 2026-11-02 대표님)
 * - 내 계정: 카카오, 네이버 연결(계정 합치기), 비밀번호 바꾸기(바꾸면 다른 곳 로그인은 끊김), 로그아웃.
 * - 직원 계정(소장님만, 요금제가 활성화된 사무소만): 만들기, 이름 바꾸기, 사용 안 함, 새 비밀번호 정해 주기.
 *   저장할 때 화면을 연 뒤 다른 곳에서 먼저 바꿨으면 "다른 분이 먼저 고쳤어요..."가 뜨고 새 내용을 다시 불러온다.
 */
import { useCallback, useEffect, useState } from 'react';
import { ACTIVE, api, type Who } from '../api.ts';
import { Button, Field, Link, Message, Page } from '../ui.tsx';

interface Account {
  user_id: string;
  role: string;
  display_name: string;
  login_id: string | null;
  status: '사용 중' | '사용 안 함';
  updated_at: string;
}

function StaffRow(props: { a: Account; reload: () => Promise<void>; setError: (m: string | null) => void; setInfo: (m: string | null) => void }) {
  const [name, setName] = useState(props.a.display_name);
  const [pw, setPw] = useState('');

  async function save(changes: Record<string, unknown>) {
    props.setInfo(null);
    const r = await api('POST', `/api/staff/${props.a.user_id}`, { ...changes, seen_updated_at: props.a.updated_at });
    props.setError(r.ok ? null : r.message);
    await props.reload();
  }

  async function newPassword() {
    const r = await api('POST', `/api/staff/${props.a.user_id}/password`, { password: pw });
    props.setError(r.ok ? null : r.message);
    if (r.ok) {
      setPw('');
      props.setInfo(`${props.a.display_name}의 새 비밀번호를 정했어요.`);
    }
  }

  return (
    <div style={{ borderTop: '1px solid #ccc', padding: '12px 0' }}>
      <p>{props.a.display_name} ({props.a.login_id ?? '아이디 없음'}), {props.a.status}</p>
      <Field label="이름" value={name} onChange={setName} />
      <Button onClick={() => void save({ display_name: name })}>이름 저장</Button>
      <Button onClick={() => void save({ status: props.a.status === '사용 중' ? '사용 안 함' : '사용 중' })}>
        {props.a.status === '사용 중' ? '사용 안 함으로' : '다시 사용'}
      </Button>
      <Field label="새 비밀번호" value={pw} onChange={setPw} type="password" autoComplete="new-password" />
      <Button onClick={() => void newPassword()} disabled={!pw}>새 비밀번호 정해 주기</Button>
    </div>
  );
}

export function AccountPage(props: { who: Who; go: (p: string) => void; onSignedOut: () => void; note: string | null }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(props.note);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [staff, setStaff] = useState({ display_name: '', login_id: '', password: '' });
  const manage = props.who.role === '소장님' && ACTIVE.has(props.who.service_status);

  const reload = useCallback(async () => {
    if (!manage) return;
    const r = await api<{ accounts: Account[] }>('GET', '/api/staff');
    if (r.ok) setAccounts(r.accounts);
  }, [manage]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function changePassword() {
    const r = await api('POST', '/api/auth/password', { current, next });
    setError(r.ok ? null : r.message);
    if (r.ok) {
      setInfo('비밀번호를 바꿨어요.');
      setCurrent('');
      setNext('');
    }
  }

  async function addStaff() {
    const r = await api('POST', '/api/staff', staff);
    setError(r.ok ? null : r.message);
    if (r.ok) {
      setStaff({ display_name: '', login_id: '', password: '' });
      await reload();
    }
  }

  async function logout() {
    await api('POST', '/api/auth/logout');
    props.onSignedOut();
  }

  return (
    <Page title="내 계정">
      <Message text={info} kind="info" />
      <p>{props.who.display_name} ({props.who.role}){props.who.login_id ? `, 아이디 ${props.who.login_id}` : ''}</p>
      <p>
        카카오: {props.who.has_kakao ? '연결됨' : <Button onClick={() => window.location.assign('/api/auth/kakao/start?mode=link')}>카카오 연결</Button>}
      </p>
      <p>
        네이버: {props.who.has_naver ? '연결됨' : <Button onClick={() => window.location.assign('/api/auth/naver/start?mode=link')}>네이버 연결</Button>}
      </p>
      {props.who.login_id && (
        <>
          <h2 style={{ fontSize: 24 }}>비밀번호 바꾸기</h2>
          <Field label="지금 비밀번호" value={current} onChange={setCurrent} type="password" autoComplete="current-password" />
          <Field label="새 비밀번호" value={next} onChange={setNext} type="password" autoComplete="new-password" />
          <Button onClick={() => void changePassword()}>비밀번호 바꾸기</Button>
        </>
      )}
      {manage && (
        <>
          <h2 style={{ fontSize: 24 }}>직원 계정</h2>
          {accounts.filter((a) => a.role !== '소장님').map((a) => (
            <StaffRow key={a.user_id} a={a} reload={reload} setError={setError} setInfo={setInfo} />
          ))}
          <div style={{ borderTop: '1px solid #ccc', paddingTop: 12 }}>
            <Field label="직원 이름" value={staff.display_name} onChange={(v) => setStaff({ ...staff, display_name: v })} />
            <Field label="직원 아이디" value={staff.login_id} onChange={(v) => setStaff({ ...staff, login_id: v.toLowerCase() })} />
            <Field label="처음 비밀번호" value={staff.password} onChange={(v) => setStaff({ ...staff, password: v })} type="password" autoComplete="new-password" />
            <Button onClick={() => void addStaff()}>직원 계정 만들기</Button>
          </div>
        </>
      )}
      <Message text={error} />
      <p>
        {ACTIVE.has(props.who.service_status) && <Link to="/" go={props.go}>처음 화면</Link>}
        <Link to="/plan" go={props.go}>요금제</Link>
      </p>
      <Button onClick={() => void logout()}>로그아웃</Button>
    </Page>
  );
}
